import { isStadiaController, listConnectedGamepads } from './gamepad.js';

export const LAST_PAD_PROFILE_KEY = 'games-tom-last-pad-profile';
export const CONTROLS_BACKUP_KEY = 'games-tom-controller-backup';

export function padProfileFromId(id = '') {
  if (isStadiaController(id)) return 'stadia';
  if (/dualsense|wireless controller/i.test(id)) return 'dualsense';
  if (/xbox|x-input|microsoft/i.test(id)) return 'xbox';
  if (/8bitdo|switch pro|pro controller/i.test(id)) return 'switch';
  return 'generic';
}

export function loadLastPadProfile() {
  try {
    return localStorage.getItem(LAST_PAD_PROFILE_KEY) || null;
  } catch {
    return null;
  }
}

export function saveLastPadProfile(profile) {
  try {
    localStorage.setItem(LAST_PAD_PROFILE_KEY, profile);
  } catch {
    /* ignore */
  }
}

/** Perfil do comando ligado agora, ou último usado. */
export function resolvePadProfile() {
  const pads = listConnectedGamepads();
  if (pads.length) {
    const profile = padProfileFromId(pads[0].id);
    saveLastPadProfile(profile);
    return profile;
  }
  return loadLastPadProfile() || 'keyboard';
}

export function backupControlsKey(systemKey, padProfile) {
  return `${systemKey}:${padProfile}`;
}

export function loadControlsBackup(systemKey, padProfile = resolvePadProfile()) {
  try {
    const raw = localStorage.getItem(CONTROLS_BACKUP_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data?.[backupControlsKey(systemKey, padProfile)] || null;
  } catch {
    return null;
  }
}

/** Última configuração guardada para a consola (comando ligado → perfil dele → último perfil → qualquer). */
export function loadBestControlsForSystem(systemKey) {
  const pads = listConnectedGamepads();
  if (pads.length) {
    const profile = padProfileFromId(pads[0].id);
    const saved = loadControlsBackup(systemKey, profile);
    if (saved && playerHasBindings(saved[0])) {
      return { controls: saved, padProfile: profile };
    }
  }

  const lastProfile = loadLastPadProfile();
  if (lastProfile) {
    const saved = loadControlsBackup(systemKey, lastProfile);
    if (saved && playerHasBindings(saved[0])) {
      return { controls: saved, padProfile: lastProfile };
    }
  }

  try {
    const raw = localStorage.getItem(CONTROLS_BACKUP_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const prefix = `${systemKey}:`;
    let best = null;
    for (const [key, controls] of Object.entries(data)) {
      if (!key.startsWith(prefix) || !controls || typeof controls !== 'object') continue;
      if (![0, 1, 2, 3].some((i) => playerHasBindings(controls[i]))) continue;
      best = { controls, padProfile: key.slice(prefix.length) };
    }
    return best;
  } catch {
    return null;
  }
}

export function saveControlsBackup(systemKey, controls, padProfile = resolvePadProfile()) {
  if (!controls || typeof controls !== 'object') return;
  try {
    const raw = localStorage.getItem(CONTROLS_BACKUP_KEY);
    const data = raw ? JSON.parse(raw) : {};
    data[backupControlsKey(systemKey, padProfile)] = controls;
    localStorage.setItem(CONTROLS_BACKUP_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
  import('./profileSync.js')
    .then(({ scheduleProfileControlsPush }) => scheduleProfileControlsPush(systemKey, padProfile))
    .catch(() => {});
}

function playerHasBindings(playerControls) {
  if (!playerControls || typeof playerControls !== 'object') return false;
  return Object.values(playerControls).some(
    (btn) => btn && (btn.value2 !== undefined && btn.value2 !== '' || btn.value !== undefined),
  );
}

export function playerHasGamepadBindings(playerControls) {
  if (!playerControls || typeof playerControls !== 'object') return false;
  return Object.values(playerControls).some(
    (btn) => btn && btn.value2 !== undefined && btn.value2 !== '',
  );
}

export function controlsNeedSeed(controls) {
  if (!controls) return true;
  return ![0, 1, 2, 3].some((i) => playerHasBindings(controls[i]));
}

export function cloneControls(controls) {
  return JSON.parse(JSON.stringify(controls));
}
